import React, { useState } from 'react';
import { Calendar, Clock, User, ArrowRight, BookOpen, Search, Filter } from 'lucide-react';
import { motion } from 'framer-motion';
import { useBlog } from '../hooks/useData';
import LoadingSpinner from './ui/LoadingSpinner';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BlogPage: React.FC = () => {
  const { t } = useTranslation();
  const { blogData, loading, error } = useBlog();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Filtrar artigos
  const filteredPosts = blogData.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === '' || post.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Pegar categorias únicas
  const categories = Array.from(new Set(blogData.map(post => post.category)));

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="min-h-screen bg-brand-sand pt-32">
      <div className="wrapper py-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <BookOpen className="w-8 h-8 text-brand-accent" />
              <h1 className="text-4xl md:text-5xl font-heading text-brand-slate">
                {t('content.title')}
              </h1>
            </div>
            <p className="text-lg text-brand-ink/90 max-w-3xl mx-auto">
              {t('content.subtitle')}
            </p>
          </motion.div>

          {/* Filtros */}
          <motion.div variants={itemVariants} className="mb-12">
            <div className="card card-pad">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Busca */}
                <div>
                  <label className="label">Buscar artigos</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-brand-ink/40" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Digite palavras-chave..."
                      className="input pl-10"
                    />
                  </div>
                </div>

                {/* Categoria */}
                <div>
                  <label className="label">Categoria</label>
                  <div className="relative">
                    <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-brand-ink/40" />
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="input pl-10 appearance-none"
                    >
                      <option value="">Todas as categorias</option>
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {loading ? (
            <LoadingSpinner message="Carregando artigos..." />
          ) : error ? (
            <div className="text-center py-8">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                <p className="text-red-600 font-medium mb-2">Erro ao carregar artigos</p>
                <p className="text-red-500 text-sm">{error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Resultados */}
              <motion.div variants={itemVariants} className="mb-8">
                <p className="text-brand-ink/70">
                  {filteredPosts.length} artigo{filteredPosts.length !== 1 ? 's' : ''} encontrado{filteredPosts.length !== 1 ? 's' : ''}
                </p>
              </motion.div>

              {/* Grid de artigos */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    variants={itemVariants}
                    className="card card-pad group hover:shadow-lg transition-shadow duration-300"
                  >
                    <Link to={`/blog/${post.slug}`} className="block no-underline">
                    {/* Imagem do artigo */}
                    <div className="aspect-video bg-gradient-to-br from-brand-accent to-brand-slate rounded-lg mb-4 flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-brand-sand" />
                    </div>

                    {/* Categoria */}
                    <div className="mb-3">
                      <span className="badge text-xs">
                        {post.category}
                      </span>
                    </div>

                    {/* Título */}
                    <h3 className="text-xl font-heading text-brand-slate mb-3 group-hover:text-brand-accent transition-colors">
                      {post.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-brand-ink/80 text-sm leading-relaxed mb-4">
                      {post.excerpt}
                    </p>

                    {/* Meta informações */}
                    <div className="flex items-center gap-4 text-xs text-brand-ink/60 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(post.date)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{post.read_time}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span>{post.author}</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {(Array.isArray(post.tags) ? post.tags : []).slice(0, 3).map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="px-2 py-1 bg-brand-slate/10 text-brand-slate text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Link para ler mais */}
                    <div className="flex items-center gap-2 text-sm text-brand-slate group-hover:text-brand-accent transition-colors">
                      Ler artigo completo
                      <ArrowRight className="w-4 h-4" />
                    </div>
                    </Link>
                  </motion.article>
                ))}
              </div>

              {/* Mensagem quando não há resultados */}
              {filteredPosts.length === 0 && (
                <motion.div variants={itemVariants} className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-brand-ink/30 mx-auto mb-4" />
                  <h3 className="text-xl font-heading text-brand-slate mb-2">
                    Nenhum artigo encontrado
                  </h3>
                  <p className="text-brand-ink/70 mb-6">
                    Tente ajustar os filtros de busca ou categoria.
                  </p>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('');
                    }}
                    className="btn-secondary"
                  >
                    Limpar filtros
                  </button>
                </motion.div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default BlogPage;
